import React from 'react';
import { notUndefined } from '../../../../../../../../shared/lib/functional';
import {
  DailyProgramEvent,
  DailyProgramVenue,
} from '../../../../../../../../shared/types/dailyProgram';
import { useData } from '../../context/DataContext';
import Tag from '../Tag';

interface TagsHocChildProps {
  tags: Array<React.ReactElement<typeof Tag>>;
}

interface TagsHocPropsBase {}

interface TagsHocPropsDefault {
  type: 'basic';
  item: { isTrending: boolean; zoneIds: string[] };
}

interface TagsHocPropsVenue {
  type: 'venue';
  item: DailyProgramVenue;
}

interface TagsHocPropsEvent {
  type: 'event';
  item: DailyProgramEvent;
}

type TagsHocProps = TagsHocPropsBase &
  (TagsHocPropsDefault | TagsHocPropsVenue | TagsHocPropsEvent) & {
    children: (props: TagsHocChildProps) => React.ReactNode;
  };

export default function TagsHoc(props: TagsHocProps) {
  const { type, item, children } = props;
  const { todayLineup, data } = useData();

  const tags: Array<React.ReactElement<typeof Tag>> = [];

  if (item.isTrending) {
    tags.push(
      <Tag
        key="tag-trending"
        className="card-tags tag-trending"
        icon={['fass', 'fire']}
        name={data.appConfig.TRENDING_LABEL}
        variant="red"
      />
    );
  }

  if (type === 'event' && item.isPriced) {
    tags.push(
      <Tag
        key="tag-priced"
        className="card-tags tag-priced"
        icon={['fass', 'dollar-circle']}
        name="Priced"
        variant="green"
      />
    );
  }

  if (type === 'venue') {
    tags.push(
      <Tag
        key="status"
        className="card-tags tag-status"
        name={item.isOpen ? 'Open' : 'Closed'}
        variant={item.isOpen ? 'green' : 'slate'}
      />
    );
    if (todayLineup.offers.find(o => o.venueId === item.id)) {
      tags.push(
        <Tag
          key="tag-offers"
          className="card-tags tag-offers"
          icon={['fass', 'hand-holding-circle-dollar']}
          name="Offers"
          variant="purple"
        />
      );
    }
  }

  const zoneTags = item.zoneIds
    .map(z => {
      const zone = data.collections.zones[z];

      return zone ? (
        <Tag
          key={`tag-zone-${z}`}
          className={`card-tags tag-zone-${z}`}
          name={zone.name}
          color={zone.color || '#ffffff'}
        />
      ) : undefined;
    })
    .filter(notUndefined);

  if (
    Object.keys(data.collections.zones).length > 0 &&
    zoneTags.length === Object.keys(data.collections.zones).length
  ) {
    tags.push(
      <Tag
        key="tag-zone-none"
        className="card-tags tag-zone-none"
        name="All Properties"
        color="#000000"
      />
    );
  } else {
    tags.push(...zoneTags);
  }

  const childProps: TagsHocChildProps = {
    tags,
  };

  return <>{children(childProps)}</>;
}
